import React, { FC, useState, useEffect } from "react";
import { 
  Card, 
  Table, 
  Button, 
  Space, 
  Tag, 
  notification, 
  Modal, 
  Upload,
  Typography,
  Input,
  Select,
  message
} from "antd";
import { 
  HomeOutlined,
  ReloadOutlined,
  UploadOutlined,
  DeleteOutlined,
  EditOutlined,
  LinkOutlined,
  BookOutlined,
  DownloadOutlined
} from '@ant-design/icons';
import { useNavigate } from 'react-router-dom';
import { getAuthToken } from '../utils/functions';
import { ReferencesUrl } from '../utils/network';
import axios from 'axios';
import type { UploadProps } from 'antd';

const { Title, Text } = Typography;
const { Option } = Select;
const { Search } = Input;

interface Reference {
  id: number;
  case_name_citation: string;
  year: number;
  legal_issue: string | null;
  opinion_url: string | null;
  basis: string | null;
  basis_display: string | null;
  created_by_name: string;
  created_at: string;
  updated_at: string;
}

interface ParsedReference {
  case_name_citation: string;
  year: number;
  legal_issue: string | null;
  opinion_url: string | null;
  basis: string | null;
  rowNumber: number;
  errors: string[];
  isValid: boolean;
}

const References: FC = () => {
  const navigate = useNavigate();
  const [references, setReferences] = useState<Reference[]>([]);
  const [loading, setLoading] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [filters, setFilters] = useState<{ basis?: string; year?: number }>({});
  const [searchText, setSearchText] = useState('');
  const [pageSize, setPageSize] = useState(20);
  const [previewModalVisible, setPreviewModalVisible] = useState(false);
  const [parsedData, setParsedData] = useState<ParsedReference[]>([]);
  const [uploadFile, setUploadFile] = useState<File | null>(null);
  const [validating, setValidating] = useState(false);

  // Restrict access to admin users only
  useEffect(() => {
    const role = localStorage.getItem('token1');
    if (!role || role.toLowerCase() !== 'admin') {
      navigate('/');
    }
  }, [navigate]);

  // Fetch references
  const fetchReferences = async () => {
    setLoading(true);
    try {
      const headers = getAuthToken();
      if (!headers) {
        notification.error({
          message: 'Error',
          description: 'Authentication required',
        });
        return;
      }

      let url = ReferencesUrl;
      const params = new URLSearchParams();
      
      if (filters.basis) {
        params.append('basis', filters.basis);
      }
      if (filters.year) {
        params.append('year', filters.year.toString());
      }
      if (searchText) {
        params.append('search', searchText);
      }

      if (params.toString()) {
        url += `?${params.toString()}`;
      }

      const response = await axios.get(url, headers);
      setReferences(response.data);
    } catch (error: any) {
      notification.error({
        message: 'Error',
        description: error.response?.data?.detail || 'Failed to fetch references',
      });
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchReferences();
  }, [filters, searchText]);

  // Parse CSV file - Simple parsing without validation
  const parseCSV = (content: string): ParsedReference[] => {
    const lines = content.split('\n').filter(line => line.trim() && !line.trim().startsWith('#'));
    if (lines.length < 2) {
      throw new Error('CSV file must have at least a header row and one data row');
    }

    // Parse header row
    const headerLine = lines[0];
    // Handle CSV with quotes and commas inside fields
    const headers: string[] = [];
    let currentHeader = '';
    let inQuotes = false;
    
    for (let i = 0; i < headerLine.length; i++) {
      const char = headerLine[i];
      if (char === '"') {
        inQuotes = !inQuotes;
      } else if (char === ',' && !inQuotes) {
        headers.push(currentHeader.trim().replace(/^"|"$/g, ''));
        currentHeader = '';
      } else {
        currentHeader += char;
      }
    }
    headers.push(currentHeader.trim().replace(/^"|"$/g, ''));
    
    // Find column indices (case-insensitive)
    const caseNameIndex = headers.findIndex(h => 
      h.toLowerCase().includes('case') && (h.toLowerCase().includes('name') || h.toLowerCase().includes('citation'))
    );
    const yearIndex = headers.findIndex(h => h.toLowerCase().includes('year'));
    const legalIssueIndex = headers.findIndex(h => 
      h.toLowerCase().includes('legal') && h.toLowerCase().includes('issue')
    );
    const opinionUrlIndex = headers.findIndex(h => 
      h.toLowerCase().includes('opinion') && h.toLowerCase().includes('url')
    );
    const basisIndex = headers.findIndex(h => h.toLowerCase().includes('basis'));

    if (caseNameIndex === -1 || yearIndex === -1) {
      throw new Error('CSV must contain "Case Name / Citation" and "Year" columns');
    }

    // Parse data rows
    const parsed: ParsedReference[] = [];
    for (let i = 1; i < lines.length; i++) {
      const line = lines[i];
      
      // Handle CSV with quotes and commas inside fields
      const values: string[] = [];
      let currentValue = '';
      let inQuotes = false;
      
      for (let j = 0; j < line.length; j++) {
        const char = line[j];
        if (char === '"') {
          inQuotes = !inQuotes;
        } else if (char === ',' && !inQuotes) {
          values.push(currentValue.trim().replace(/^"|"$/g, ''));
          currentValue = '';
        } else {
          currentValue += char;
        }
      }
      values.push(currentValue.trim().replace(/^"|"$/g, ''));
      
      // Extract values
      const caseName = caseNameIndex >= 0 ? (values[caseNameIndex] || '') : '';
      const yearStr = yearIndex >= 0 ? (values[yearIndex] || '') : '';
      // Try to parse year, but don't validate - just use the string if parsing fails
      let year = 0;
      if (yearStr) {
        const parsedYear = parseInt(yearStr.trim());
        year = isNaN(parsedYear) ? 0 : parsedYear;
      }
      
      const legalIssue = legalIssueIndex >= 0 ? (values[legalIssueIndex] || null) : null;
      const opinionUrl = opinionUrlIndex >= 0 ? (values[opinionUrlIndex] || null) : null;
      const basis = basisIndex >= 0 ? (values[basisIndex] || null) : null;

      parsed.push({
        case_name_citation: caseName,
        year: year || 0,
        legal_issue: legalIssue,
        opinion_url: opinionUrl,
        basis,
        rowNumber: i + 1,
        errors: [], // No validation errors
        isValid: true, // All rows are considered valid for display
      });
    }

    return parsed;
  };

  // Handle file parsing and preview
  const handleFileParse = async (file: File) => {
    setValidating(true);
    try {
      const fileName = file.name.toLowerCase();
      
      if (fileName.endsWith('.csv')) {
        // Parse CSV
        const reader = new FileReader();
        reader.onload = (e) => {
          try {
            const content = e.target?.result as string;
            const parsed = parseCSV(content);
            setParsedData(parsed);
            setUploadFile(file);
            setPreviewModalVisible(true);
          } catch (error: any) {
            notification.error({
              message: 'Parse Error',
              description: error.message || 'Failed to parse CSV file',
            });
          } finally {
            setValidating(false);
          }
        };
        reader.onerror = () => {
          notification.error({
            message: 'File Read Error',
            description: 'Failed to read the file',
          });
          setValidating(false);
        };
        reader.readAsText(file);
      } else if (fileName.endsWith('.xlsx') || fileName.endsWith('.xls')) {
        // For Excel files, we'll need to upload to backend for parsing
        // Or we can add xlsx library. For now, show a message
        notification.warning({
          message: 'Excel File Detected',
          description: 'Excel file parsing will be handled on the server. Please proceed with upload.',
          duration: 3,
        });
        // For Excel, directly upload (backend handles parsing)
        handleDirectUpload(file);
        setValidating(false);
      } else {
        notification.error({
          message: 'Invalid File Type',
          description: 'Please upload a CSV or Excel file',
        });
        setValidating(false);
      }
    } catch (error: any) {
      notification.error({
        message: 'Error',
        description: error.message || 'Failed to process file',
      });
      setValidating(false);
    }
  };

  // Handle direct upload (for Excel or when user confirms CSV)
  const handleDirectUpload = async (file: File) => {
    setUploading(true);
    try {
      const headers = getAuthToken();
      if (!headers) {
        notification.error({
          message: 'Error',
          description: 'Authentication required',
        });
        return;
      }

      const formData = new FormData();
      formData.append('file', file);

      const response = await axios.post(
        `${ReferencesUrl}upload/`,
        formData,
        {
          headers: {
            ...headers.headers,
          },
        }
      );

      if (response.data.success) {
        notification.success({
          message: 'Upload Successful',
          description: response.data.message || `Created: ${response.data.created}, Errors: ${response.data.errors}`,
          duration: 5,
        });
        
        // Show error details if any
        if (response.data.error_details && response.data.error_details.length > 0) {
          console.error('Upload errors:', response.data.error_details);
          Modal.warning({
            title: 'Upload Completed with Errors',
            content: (
              <div>
                <p>{response.data.message}</p>
                <ul style={{ maxHeight: '300px', overflowY: 'auto' }}>
                  {response.data.error_details.slice(0, 20).map((error: string, index: number) => (
                    <li key={index} style={{ fontSize: '12px', marginBottom: '4px' }}>{error}</li>
                  ))}
                  {response.data.error_details.length > 20 && (
                    <li style={{ fontSize: '12px', color: '#999' }}>
                      ... and {response.data.error_details.length - 20} more errors
                    </li>
                  )}
                </ul>
              </div>
            ),
            width: 600,
          });
        }
        
        fetchReferences();
        setPreviewModalVisible(false);
        setParsedData([]);
        setUploadFile(null);
      } else {
        // Show detailed error information
        const errorMessage = response.data.message || 'Failed to upload file';
        const errorDetails = response.data.error_details || [];
        
        notification.error({
          message: 'Upload Failed',
          description: errorMessage,
          duration: 8,
        });
        
        // Show error details modal if there are specific errors
        if (errorDetails.length > 0) {
          Modal.error({
            title: 'Upload Failed - Error Details',
            content: (
              <div>
                <p>{errorMessage}</p>
                <p><strong>Created: {response.data.created || 0} records</strong></p>
                <p><strong>Errors: {response.data.errors || 0} rows</strong></p>
                <ul style={{ maxHeight: '300px', overflowY: 'auto', marginTop: '16px' }}>
                  {errorDetails.slice(0, 30).map((error: string, index: number) => (
                    <li key={index} style={{ fontSize: '12px', marginBottom: '4px', color: '#ff4d4f' }}>{error}</li>
                  ))}
                  {errorDetails.length > 30 && (
                    <li style={{ fontSize: '12px', color: '#999' }}>
                      ... and {errorDetails.length - 30} more errors
                    </li>
                  )}
                </ul>
              </div>
            ),
            width: 700,
          });
        }
      }
    } catch (error: any) {
      notification.error({
        message: 'Upload Error',
        description: error.response?.data?.message || 'Failed to upload file. Please try again.',
      });
    } finally {
      setUploading(false);
    }
  };

  // Handle file upload
  const uploadProps: UploadProps = {
    name: 'file',
    accept: '.csv,.xlsx,.xls',
    showUploadList: false,
    beforeUpload: (file) => {
      const isValidType = file.type === 'text/csv' || 
                         file.name.endsWith('.csv') || 
                         file.name.endsWith('.xlsx') || 
                         file.name.endsWith('.xls');
      if (!isValidType) {
        message.error('Please upload a CSV or Excel file');
        return false;
      }
      handleFileParse(file);
      return false; // Prevent auto upload
    },
  };

  // Handle confirm upload from preview modal
  const handleConfirmUpload = () => {
    if (!uploadFile) return;
    
    if (parsedData.length === 0) {
      notification.warning({
        message: 'No Data',
        description: 'No data rows found in the file',
      });
      return;
    }

    // Upload the file - backend will handle validation
    handleDirectUpload(uploadFile);
  };

  // Download CSV template
  const handleDownloadTemplate = () => {
    // Create CSV content with headers and sample row
    const headers = [
      'Case Name / Citation',
      'Year',
      'Legal Issue',
      'Opinion URL',
      'Basis'
    ];
    
    // Sample row with examples
    const sampleRow = [
      'Smith v. Company, 123 F.3d 456 (2020)',
      '2020',
      'Age discrimination in hiring',
      'https://example.com/opinion.pdf',
      'Age'
    ];
    
    // Create CSV content
    const csvContent = [
      headers.join(','),
      sampleRow.join(','),
      '', // Empty line
      '# Instructions:',
      '# 1. Case Name / Citation: Required - Full case name and citation',
      '# 2. Year: Required - Year the case was decided (e.g., 2020)',
      '# 3. Legal Issue: Optional - Brief description of the legal issue',
      '# 4. Opinion URL: Optional - URL to the full legal opinion',
      '# 5. Basis: Optional - Discrimination basis (Age, Color, Mental/Physical Handicap, National Origin, Race, Religion, Reprisal, Sex)',
      '#',
      '# Note: Remove the instruction lines (lines starting with #) before uploading'
    ].join('\n');
    
    // Create blob and download
    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const link = document.createElement('a');
    const url = URL.createObjectURL(blob);
    
    link.setAttribute('href', url);
    link.setAttribute('download', 'references_template.csv');
    link.style.visibility = 'hidden';
    
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    
    notification.success({
      message: 'Template Downloaded',
      description: 'CSV template has been downloaded. Fill in your data and upload.',
      duration: 3,
    });
  };

  // Handle delete
  const handleDelete = async (id: number) => {
    Modal.confirm({
      title: 'Delete Reference',
      content: 'Are you sure you want to delete this reference?',
      okText: 'Delete',
      okType: 'danger',
      onOk: async () => {
        try {
          const headers = getAuthToken();
          if (!headers) {
            notification.error({
              message: 'Error',
              description: 'Authentication required',
            });
            return;
          }

          await axios.delete(`${ReferencesUrl}${id}/`, headers);
          notification.success({
            message: 'Success',
            description: 'Reference deleted successfully',
          });
          fetchReferences();
        } catch (error: any) {
          notification.error({
            message: 'Error',
            description: error.response?.data?.detail || 'Failed to delete reference',
          });
        }
      },
    });
  };

  // Columns for table
  const columns = [
    {
      title: 'Case Name / Citation',
      dataIndex: 'case_name_citation',
      key: 'case_name_citation',
      ellipsis: true,
      width: '30%',
    },
    {
      title: 'Year',
      dataIndex: 'year',
      key: 'year',
      width: '10%',
      sorter: (a: Reference, b: Reference) => a.year - b.year,
    },
    {
      title: 'Legal Issue',
      dataIndex: 'legal_issue',
      key: 'legal_issue',
      ellipsis: true,
      width: '25%',
      render: (text: string | null) => text || '-',
    },
    {
      title: 'Basis',
      dataIndex: 'basis_display',
      key: 'basis',
      width: '15%',
      render: (text: string | null, record: Reference) => 
        record.basis ? <Tag color="blue">{text || record.basis}</Tag> : '-',
      filters: [
        { text: 'Age', value: 'Age' },
        { text: 'Color', value: 'Color' },
        { text: 'Mental/Physical Handicap', value: 'Mental/Physical Handicap' },
        { text: 'National Origin', value: 'National Origin' },
        { text: 'Race', value: 'Race' },
        { text: 'Religion', value: 'Religion' },
        { text: 'Reprisal', value: 'Reprisal' },
        { text: 'Sex', value: 'Sex' },
      ],
      onFilter: (value: any, record: Reference) => record.basis === value,
    },
    {
      title: 'Opinion URL',
      dataIndex: 'opinion_url',
      key: 'opinion_url',
      width: '15%',
      render: (url: string | null) => 
        url ? (
          <a href={url} target="_blank" rel="noopener noreferrer">
            <LinkOutlined /> View
          </a>
        ) : '-',
    },
    {
      title: 'Actions',
      key: 'actions',
      width: '10%',
      render: (_: any, record: Reference) => (
        <Space>
          <Button
            type="link"
            danger
            icon={<DeleteOutlined />}
            onClick={() => handleDelete(record.id)}
            size="small"
          >
            Delete
          </Button>
        </Space>
      ),
    },
  ];

  return (
    <div style={{ padding: '24px' }}>
      {/* Header */}
      <Card style={{ marginBottom: '24px' }}>
        <Space style={{ width: '100%', justifyContent: 'space-between' }}>
          <div>
            <Title level={2} style={{ margin: 0 }}>
              <BookOutlined /> References
            </Title>
            <Text type="secondary">
              Manage legal case references and citations
            </Text>
          </div>
          <Space>
            <Button
              icon={<HomeOutlined />}
              onClick={() => navigate('/')}
            >
              Home
            </Button>
            <Button 
              icon={<ReloadOutlined />} 
              onClick={fetchReferences}
              loading={loading}
            >
              Refresh
            </Button>
            <Button 
              icon={<DownloadOutlined />}
              onClick={handleDownloadTemplate}
            >
              Data Template
            </Button>
            <Upload {...uploadProps}>
              <Button 
                type="primary" 
                icon={<UploadOutlined />} 
                loading={uploading || validating}
              >
                {validating ? 'Validating...' : 'Upload CSV/Excel'}
              </Button>
            </Upload>
          </Space>
        </Space>
      </Card>

      {/* Filters */}
      <Card style={{ marginBottom: '24px' }}>
        <Space wrap>
          <Search
            placeholder="Search case name or legal issue"
            allowClear
            onSearch={(value) => setSearchText(value)}
            style={{ width: 300 }}
          />
          <Select
            placeholder="Filter by Basis"
            allowClear
            style={{ width: 200 }}
            onChange={(value) => setFilters({ ...filters, basis: value || undefined })}
          >
            <Option value="Age">Age</Option>
            <Option value="Color">Color</Option>
            <Option value="Mental/Physical Handicap">Mental/Physical Handicap</Option>
            <Option value="National Origin">National Origin</Option>
            <Option value="Race">Race</Option>
            <Option value="Religion">Religion</Option>
            <Option value="Reprisal">Reprisal</Option>
            <Option value="Sex">Sex</Option>
          </Select>
          <Input
            placeholder="Filter by Year"
            type="number"
            style={{ width: 150 }}
            onChange={(e) => {
              const year = e.target.value ? parseInt(e.target.value) : undefined;
              setFilters({ ...filters, year });
            }}
          />
        </Space>
      </Card>

      {/* Table */}
      <Card>
        <Table
          columns={columns}
          dataSource={references}
          loading={loading}
          rowKey="id"
          pagination={{
            pageSize: pageSize,
            showSizeChanger: true,
            showTotal: (total) => `Total ${total} references`,
            pageSizeOptions: ['10', '20', '50', '100'],
            onShowSizeChange: (current, size) => setPageSize(size),
          }}
          scroll={{ x: 'max-content' }}
        />
      </Card>

      {/* Preview Modal */}
      <Modal
        title="Review Upload Data"
        open={previewModalVisible}
        onCancel={() => {
          setPreviewModalVisible(false);
          setParsedData([]);
          setUploadFile(null);
        }}
        width={1000}
        footer={[
          <Button
            key="cancel"
            onClick={() => {
              setPreviewModalVisible(false);
              setParsedData([]);
              setUploadFile(null);
            }}
          >
            Cancel
          </Button>,
          <Button
            key="submit"
            type="primary"
            onClick={handleConfirmUpload}
            loading={uploading}
            disabled={parsedData.length === 0}
          >
            Submit ({parsedData.length} rows)
          </Button>,
        ]}
      >
        <div style={{ marginBottom: '16px' }}>
          <Space>
            <Text strong>Total Rows:</Text>
            <Tag>{parsedData.length}</Tag>
            <Text type="secondary">Review the data below and click Submit to upload</Text>
          </Space>
        </div>
        <Table
          columns={[
            {
              title: 'Row',
              dataIndex: 'rowNumber',
              key: 'rowNumber',
              width: 60,
            },
            {
              title: 'Case Name / Citation',
              dataIndex: 'case_name_citation',
              key: 'case_name_citation',
              width: 200,
              ellipsis: true,
            },
            {
              title: 'Year',
              dataIndex: 'year',
              key: 'year',
              width: 80,
            },
            {
              title: 'Legal Issue',
              dataIndex: 'legal_issue',
              key: 'legal_issue',
              width: 150,
              ellipsis: true,
              render: (text: string | null) => text || '-',
            },
            {
              title: 'Basis',
              dataIndex: 'basis',
              key: 'basis',
              width: 120,
              render: (text: string | null) => text ? <Tag>{text}</Tag> : '-',
            },
            {
              title: 'Opinion URL',
              dataIndex: 'opinion_url',
              key: 'opinion_url',
              width: 150,
              ellipsis: true,
              render: (url: string | null) => url ? (
                <a href={url} target="_blank" rel="noopener noreferrer">
                  <LinkOutlined /> View
                </a>
              ) : '-',
            },
          ]}
          dataSource={parsedData}
          rowKey="rowNumber"
          pagination={{
            pageSize: 10,
            showSizeChanger: true,
            showTotal: (total) => `Total ${total} rows`,
          }}
          scroll={{ y: 400 }}
        />
      </Modal>
    </div>
  );
};

export default References;

